import React from "react";
import { Card, Table, Spinner, Alert } from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import { useRequest } from "../hooks/useRequest";
import { getSeasonStatsUrl } from "../config/api";
import { TeamStat } from "../types/models";
import TeamDisplay from "../Common/TeamDisplay";

interface SeasonStandingProps {
  seasonId: number;
  title?: string;
  showCard?: boolean;
  gameType?: "regular" | "exhibition" | "playoff";
}

function SeasonStanding({
  seasonId,
  title = "Standings",
  showCard = true,
  gameType,
}: SeasonStandingProps) {
  const auth = useAuth();
  const { data, error } = useRequest<TeamStat[]>(
    getSeasonStatsUrl(seasonId, gameType),
    auth.token,
  );

  // Sort by points descending, then by goals_for descending as tiebreaker
  const sortedData = React.useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => {
      if (b.points !== a.points) {
        return b.points - a.points;
      }
      // Tiebreaker: goals differential
      const aDiff = a.goals_for - a.goals_against;
      const bDiff = b.goals_for - b.goals_against;
      if (bDiff !== aDiff) {
        return bDiff - aDiff;
      }
      // Second tiebreaker: goals for
      return b.goals_for - a.goals_for;
    });
  }, [data]);

  const content = (
    <>
      {error && (
        <Alert variant="danger">Error loading standings: {error.message}</Alert>
      )}
      {!data && !error && (
        <div className="text-center py-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <div className="mt-2">Loading standings...</div>
        </div>
      )}
      {data && sortedData.length === 0 && (
        <Alert variant="info">No standings data available yet.</Alert>
      )}
      {data && sortedData.length > 0 && (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>#</th>
              <th>Team</th>
              <th className="text-center">GP</th>
              <th className="text-center">W</th>
              <th className="text-center">L</th>
              <th className="text-center">T</th>
              <th className="text-center">GF</th>
              <th className="text-center">GA</th>
              <th className="text-center">+/-</th>
              <th className="text-center">PTS</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((team, index) => (
              <tr key={team.team_id}>
                <td>{index + 1}</td>
                <td>
                  <TeamDisplay
                    team={{
                      id: team.team_id,
                      name: team.team_name,
                      colour_code: team.colour_code,
                    }}
                  />
                </td>
                <td className="text-center">{team.games_played}</td>
                <td className="text-center">{team.wins}</td>
                <td className="text-center">{team.losses}</td>
                <td className="text-center">{team.ties}</td>
                <td className="text-center">{team.goals_for}</td>
                <td className="text-center">{team.goals_against}</td>
                <td className="text-center">
                  {team.goals_for - team.goals_against > 0 ? "+" : ""}
                  {team.goals_for - team.goals_against}
                </td>
                <td className="text-center">
                  <strong>{team.points}</strong>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );

  if (!showCard) {
    return content;
  }

  return (
    <Card>
      <Card.Header>
        <Card.Title className="mb-0">{title}</Card.Title>
      </Card.Header>
      <Card.Body>{content}</Card.Body>
    </Card>
  );
}

export default SeasonStanding;
